/** 지식 파일 02: 이론과 기법 기준표 (검증된 이론 위주, 방법·말 예시 포함) */
export const THEORIES_MD = `# 02 이론과 기법 기준표 (v1)

이 파일은 상담 분석·계획·문서 초안에서 기법을 제안하고 기록할 때의 기준이다.
- 기법을 쓸 때는 항상 **기법 — 이론(대표 학자) — 이 사람에게 쓰는 이유 — 진행 방법 — 상담자 말 예시 — 주의점**을 함께 쓴다.
- 경험적 근거가 쌓인 이론을 우선한다. 근거 수준은 대략적인 안내다 (강함: 여러 무작위 대조연구·메타분석 / 중간: 일부 연구 / 실무 중심: 임상 전통·사례 연구 위주).
- 한 이론에 치우치지 않는다. 사례개념화와 내담자의 준비도·선호·문화(신앙 포함)에 맞춘다.
- **이 표는 출발점이다.** 사례에 더 맞는 검증된 이론·기법(예: 메타인지치료 Adrian Wells, 마음챙김 기반 스트레스 감소 Jon Kabat-Zinn, 정서중심 부부치료 Sue Johnson 등)이 있으면 표 밖의 것도 쓰고, 대표 학자·대표 저서(연도)와 "(기준표 외)"를 함께 적는다.
- 외상 치료(EMDR·지속노출 등)처럼 별도 수련이 필요한 기법은 "전문 훈련 필요"를 밝히고, 의뢰 여부를 함께 검토한다.

---

## A. 공통 기반 (모든 접근의 바탕)

### A1. 핵심 조건 — 인간중심 (Carl Rogers)
- 가정: 공감적 이해·무조건적 긍정적 존중·진실성이 있는 관계에서 내담자는 스스로 성장한다.
- 쓰는 때: 모든 회기, 특히 초기 라포·수치심·자기비판이 큰 내담자.
- 방법: 판단·조언을 늦추고 내담자의 내적 준거틀에서 의미와 감정을 반영한다.
- 말 예시: "그 말을 들었을 때 마음이 많이 무너지셨겠어요." / "스스로를 그렇게까지 몰아붙이게 되는 게 참 지치는 일이었겠네요."
- 주의: 반영이 기계적 되풀이가 되지 않게, 내담자가 아직 말로 하지 않은 감정 한 걸음만 앞서 짚는다.
- 근거: 강함 (공통요인 연구)

### A2. 조력 기술 3단계 — 탐색·통찰·실행 (Clara Hill)
- 가정: 탐색(이야기·감정) → 통찰(의미·패턴) → 실행(변화 행동) 순서로 돕는다.
- 방법: 탐색 단계 — 개방형 질문, 재진술, 감정 반영 / 통찰 단계 — 도전(직면), 해석, 즉시성, 자기개방 / 실행 단계 — 정보 제공, 과제, 역할연습.
- 말 예시: (탐색) "그 순간을 조금 더 자세히 말씀해 주시겠어요?" (통찰) "실수할 때마다 '나는 부족하다'로 바로 가시는 것 같은데, 언제부터 그러셨을까요?" (실행) "이번 주에 그 생각이 떠오를 때 한 줄만 적어 와 보실래요?"
- 주의: 탐색이 충분하지 않은 채 해석·실행으로 서두르지 않는다.
- 근거: 중간~강함 (상담자 교육 연구)

### A3. 작업동맹과 균열-회복 (Edward Bordin / Jeremy Safran · J. Christopher Muran)
- 가정: 목표·과제 합의와 정서적 유대가 성과를 예측한다. 균열(위축·대립)을 다루면 동맹이 더 단단해진다.
- 방법: 균열 신호(짧아진 대답, 화제 전환, 지나친 동의, 불만) 알아차리기 → 상담자가 먼저 지금-여기에서 묻기 → 상담자 기여 인정 → 내담자의 욕구 탐색.
- 말 예시: "방금 제 질문 뒤로 말씀이 짧아지신 것 같아요. 제가 너무 앞서 갔을까요?"
- 근거: 강함

---

## B. 인지행동 계열

### B1. 인지치료 / 인지행동치료 (Aaron T. Beck, Judith S. Beck)
- 가정: 상황 → 자동적 사고 → 감정·행동·신체. 자동적 사고 밑에 중간믿음(규칙·가정)과 핵심믿음(나·타인·세상)이 있다.
- 쓰는 때: 우울, 불안, 자기비판, 완벽주의, 반추.
- 방법: ① 구체적 상황 하나 고르기 ② 그때 떠오른 생각과 감정(0~100) 찾기 ③ 소크라테스식 질문으로 증거·대안 검토 ④ 균형 잡힌 생각 만들고 감정 재평가 ⑤ 하향 화살표로 핵심믿음 탐색 ⑥ 사고기록지·행동실험 과제.
- 말 예시: "그때 머릿속을 스친 문장을 그대로 말해 보면요?" / "그 생각이 100% 사실이라는 증거와, 그렇지 않을 수도 있다는 증거를 같이 찾아볼까요?" / "만약 그게 사실이라면, 그게 나에 대해 무엇을 말해 주는 것 같으세요?" (하향 화살표)
- 주의: 감정이 충분히 다뤄지기 전에 생각 바꾸기로 서두르면 "또 내가 틀렸다"로 들릴 수 있다.
- 근거: 강함

### B2. 행동활성화 (Neil S. Jacobson, Christopher Martell)
- 방법: 활동-기분 기록 → 가치와 연결된 작은 활동 계획 → 회피 패턴(TRAP→TRAC) 확인.
- 말 예시: "이번 주에 기분이 조금이라도 나았던 시간에는 무엇을 하고 계셨어요?"
- 근거: 강함 (우울)

### B3. 합리적 정서행동치료 REBT (Albert Ellis)
- 방법: A(사건)-B(신념)-C(결과) 찾기 → D(논박: 논리·현실·실용) → E(효과적 새 신념). 당위("반드시 ~해야")·파국화·자기비하를 겨냥.
- 말 예시: "'절대 실수하면 안 된다'는 규칙은 어디에 쓰여 있을까요?"
- 주의: 논박이 공격으로 느껴지지 않게 관계가 먼저.
- 근거: 중간

### B4. 문제해결치료 (Thomas D'Zurilla, Arthur Nezu)
- 방법: 문제 정의 → 대안 많이 만들기 → 장단점 평가 → 실행 계획 → 점검. 진로·결정 고민에 유용.
- 근거: 중간~강함

---

## C. 마음챙김·수용·자비 (3세대)

### C1. 수용전념치료 ACT (Steven C. Hayes)
- 가정: 고통스러운 생각·감정과 싸울수록 삶이 좁아진다. 심리적 유연성(수용, 탈융합, 현재 순간, 맥락적 자기, 가치, 전념 행동)을 키운다.
- 방법: 탈융합 — "나는 ~라는 생각을 하고 있다"로 말하기 / 가치 명료화 — 무엇을 위해 사는가 / 전념 행동 — 가치 방향의 작은 걸음.
- 말 예시: "'나는 부족하다'는 생각이 또 찾아왔네요. 그 생각이 시키는 대로 하면 어디로 가게 되나요?" / "그 생각이 있어도, 지금 중요하게 여기는 쪽으로 한 걸음 가 본다면?"
- 근거: 강함~중간

### C2. 자비중심치료 CFT (Paul Gilbert)
- 가정: 위협-추동-진정의 세 정서 체계. 자기비판이 강한 사람은 진정(자기자비) 체계가 약하다.
- 쓰는 때: 수치심, 강한 자기비판, 완벽주의.
- 방법: 세 체계 심리교육 → 자기비판의 기능(나를 지키려던 방식) 이해 → 자비로운 자기 상상·편지 쓰기 → 진정 호흡.
- 말 예시: "그렇게 스스로를 몰아붙이는 목소리는, 원래 무엇으로부터 나를 지키려고 생겼을까요?" / "아끼는 후배가 같은 실수를 했다면 뭐라고 말해 주시겠어요?"
- 근거: 중간 (늘어나는 중)

### C3. 변증법적 행동치료 DBT 기술 (Marsha M. Linehan)
- 방법: 마음챙김, 고통 감내(TIPP 등), 정서 조절, 대인관계 효율성(DEAR MAN) 기술 훈련.
- 쓰는 때: 정서 조절 어려움, 충동성, 자해. (전체 DBT 프로그램은 전문 훈련 필요)
- 근거: 강함

### C4. 마음챙김 기반 인지치료 MBCT (Zindel Segal, Mark Williams, John Teasdale)
- 쓰는 때: 반복 우울 재발 예방, 반추.
- 근거: 강함 (재발 예방)

---

## D. 정서·경험 중심

### D1. 정서중심치료 EFT (Leslie S. Greenberg)
- 가정: 정서는 변화의 핵심. 일차 정서에 닿고, 부적응 정서를 다른 정서로 변화시킨다.
- 표식과 과제: 자기비판 분열 → 두 의자 대화 / 미해결 과제(중요한 타인) → 빈 의자 대화 / 문제 반응(이해 안 되는 내 반응) → 체계적 환기적 펼치기 / 불분명한 느낌 → 포커싱.
- 말 예시: "비판하는 쪽이 지금 저 의자에 앉아 있다면 뭐라고 하나요? … 이제 이쪽 의자로 와서, 그 말을 들은 마음으로 답해 보세요."
- 주의: 정서 강도가 높아질 수 있으므로 동맹·안전을 먼저 확인.
- 근거: 강함~중간

### D2. 포커싱 (Eugene Gendlin)
- 방법: 몸에서 느껴지는 감각(느낌감각)에 머물고, 꼭 맞는 말을 찾는다.
- 말 예시: "그 이야기를 할 때 몸 어디에서 무언가가 느껴지나요? 잠시 거기에 머물러 볼까요?"
- 근거: 중간

### D3. 게슈탈트 (Fritz Perls)
- 방법: 지금-여기 알아차림, 빈 의자, 미해결 과제 완결.
- 근거: 실무 중심~중간

---

## E. 정신역동·관계·애착

### E1. 애착 이론 (John Bowlby, Mary Ainsworth)
- 방법: 초기 양육자 관계와 현재 관계 패턴 연결, 상담관계를 안전기지로.
- 말 예시: "어릴 때 실수했을 때 집에서는 어떤 반응을 받았나요?"
- 근거: 이론적 기반 강함

### E2. 대상관계 (D. W. Winnicott, Otto Kernberg)
- 핵심: 내면화된 자기-타인 표상, 안아주는 환경, 분열.
- 근거: 실무 중심~중간

### E3. 단기 역동 — 갈등의 삼각형·사람의 삼각형 (David Malan)
- 방법: 감정 - 불안 - 방어의 삼각형을, 현재 관계·과거 관계·상담자와의 관계에서 연결해 보여 준다.
- 말 예시: "화가 날 법한 순간에 오히려 '제가 부족해서요'라고 하시는 것 같아요. 화를 느끼는 게 어떤 점에서 불편하실까요?"
- 근거: 중간

### E4. 대인관계치료 IPT (Gerald Klerman, Myrna Weissman)
- 방법: 증상을 대인관계 영역(애도, 역할 갈등, 역할 전환, 대인관계 결핍)과 연결해 다룬다. 진로·사역 전환기에 "역할 전환" 영역이 유용.
- 근거: 강함 (우울)

### E5. 정신화 (Peter Fonagy, Anthony Bateman)
- 방법: 나와 타인의 마음 상태를 궁금해하도록 돕는다 ("그때 그분은 무슨 마음이었을까요?").
- 근거: 중간~강함

---

## F. 해결·동기

### F1. 해결중심 단기치료 SFBT (Steve de Shazer, Insoo Kim Berg)
- 방법: 예외 질문, 기적 질문, 척도 질문(0~10), 대처 질문, 칭찬.
- 말 예시: "자기비판이 조금이라도 덜했던 날은 무엇이 달랐나요?" / "지금이 4라면, 5가 되면 무엇이 달라져 있을까요?"
- 근거: 중간

### F2. 동기강화상담 MI (William R. Miller, Stephen Rollnick)
- 방법: OARS(열린 질문·인정·반영·요약), 변화대화 이끌기, 양가감정 탐색, 저항과 함께 구르기.
- 말 예시: "한편으로는 전임 사역을 원하시고, 한편으로는 두려움도 크시네요. 두 마음을 다 들어 볼까요?"
- 근거: 강함 (행동 변화·중독)

---

## G. 가족·체계·이야기

### G1. 보웬 가족체계 (Murray Bowen)
- 핵심: 자기분화, 삼각관계, 다세대 전수, 정서적 단절. 가계도(Genogram).
- 방법: 3세대 가계도 그리기 → 반복 패턴·삼각관계 찾기 → 반응적이지 않은 "나 입장" 연습.
- 근거: 실무 중심~중간

### G2. 구조적 가족치료 (Salvador Minuchin)
- 핵심: 경계·위계·하위체계, 밀착과 유리.
- 근거: 중간

### G3. 이야기치료 (Michael White, David Epston)
- 방법: 문제의 외재화("자기비판이라는 녀석"), 독특한 결과 찾기, 대안 이야기 두텁게 하기, 정의 예식.
- 말 예시: "그 '부족함의 목소리'가 가장 크게 말할 때는 언제인가요? 그 목소리에 지지 않았던 때도 있었나요?"
- 근거: 중간

---

## H. 실존·의미

### H1. 실존치료 (Irvin D. Yalom) / 의미치료 (Viktor E. Frankl)
- 핵심: 죽음·자유·고립·무의미의 실존적 관심, 책임과 선택, 의미 발견.
- 말 예시: "이 고민이 결국 어떤 삶을 살고 싶은지에 대한 질문처럼 들려요."
- 근거: 실무 중심~중간

---

## I. 외상 (전문 훈련 필요)
- 외상중심 인지행동치료 TF-CBT (Judith Cohen 외) / 인지처리치료 CPT (Patricia Resick) / 지속노출 PE (Edna Foa) / EMDR (Francine Shapiro): 근거 강함. 일반 회기에서는 안정화·심리교육·자원 강화까지, 본격 처리 작업은 훈련·수퍼비전·의뢰를 검토.

## J. 도식치료 (Jeffrey Young)
- 핵심: 초기 부적응 도식(예: 결함/수치심, 엄격한 기준), 도식 양식, 제한된 재양육.
- 쓰는 때: 오래된 성격적 패턴, 반복되는 관계 문제.
- 근거: 중간~강함 (성격 문제)

## K. 강점·긍정심리 (Martin Seligman 외)
- 방법: 강점 찾기, 감사·의미 기록. 보조적으로.
- 근거: 중간

---

## L. 목회상담

### L1. 살아 있는 인간 문서 (Anton Boisen)
- 내담자의 삶을 신학적으로 읽어야 할 "살아 있는 인간 문서"로 본다. 위기는 성장의 계기가 될 수 있다.

### L2. 목자적 관점 (Seward Hiltner)
- 치유·지탱·인도의 목회적 기능. 상담 상황을 신학적 성찰의 자리로.

### L3. 성장 상담·전인적 상담 (Howard Clinebell)
- 몸·마음·관계·사회·영성의 전인적 성장. ABC 위기상담(A 관계 형성·B 핵심 문제 파악·C 대처 행동).
- 말 예시: "지금 이 어려움 속에서 하나님과의 관계는 어떻게 느껴지세요?" (내담자가 신앙을 자원으로 여길 때만)

### L4. 목회적 돌봄 (Wayne Oates)
- 목회자의 정체성과 관계 속 돌봄, 위기·애도 돌봄.

### L5. 재구성과 희망 (Donald Capps)
- 재구성(reframing)으로 문제를 다르게 보게 하기, 희망의 작용.

### L6. 해석학적 목회상담 (Charles Gerkin)
- 내담자의 이야기와 기독교 전통의 이야기를 대화시키는 해석학적 접근.

### L7. 단기 목회상담 (Howard Stone)
- 해결중심을 목회 맥락에 적용, 짧은 회기에서 구체적 변화.

### L8. 영적 평가 (Christina Puchalski의 FICA)
- F 신앙·의미 / I 중요성 / C 공동체 / A 상담에서 다루기를 원하는가. 신앙을 상담에 쓸지 내담자에게 묻고 합의한다.

### 신앙 사용 원칙
- 내담자가 원할 때만 기도·성경을 사용한다. 신앙을 병리화하거나, 반대로 문제를 덮는 데 쓰지 않는다 ("더 믿으면 된다").
- 신앙적 자기비판(하나님 앞에서의 수치)과 건강한 죄책을 구분해 본다.

---

## M. 주호소별 출발점 (조합 예시, 사례개념화에 맞게 고른다)
| 주호소 | 1차 고려 | 함께 쓰기 |
|---|---|---|
| 우울 | 인지치료(Beck), 행동활성화 | IPT, MBCT(재발 예방), 자비중심 |
| 불안·걱정 | 인지행동(노출 포함), ACT | 마음챙김, 문제해결 |
| 강한 자기비판·수치심·완벽주의 | 자비중심(Gilbert), 인지치료(핵심믿음) | 정서중심 두 의자(Greenberg), 도식치료 |
| 관계 반복 패턴 | 정서중심·애착, 단기 역동 | 보웬(가계도), 정신화 |
| 진로·결정·전환기 | 동기강화(양가감정), 문제해결 | IPT 역할 전환, 실존·의미, 해결중심 |
| 애도 | 의미 재구성, 애도 과업(William Worden) | IPT 애도, 목회적 돌봄 |
| 외상 | 안정화·심리교육 | 전문 외상치료 의뢰 검토 |
| 신앙 위기·영적 고통 | 해석학적 목회상담(Gerkin), 영적 평가(FICA) | 실존치료, 성장 상담(Clinebell) |
`;
